import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'
import { z } from 'zod'

import { rootRoute } from './root'
import { DashboardLayout } from './dashboard-layout'
import { requireAuth, redirectIfAuthed } from './guards'
import { NotFound, RouteError, RoutePending } from './states'

/**
 * Árvore de rotas — porta do App Router (`src/app/**`) da versão Next.js.
 *
 * Equivalências:
 *   src/app/page.tsx                 → indexRoute (redirect para /dashboard)
 *   src/app/(auth)/*                 → rotas públicas, guard `redirectIfAuthed`
 *   src/app/(dashboard)/layout.tsx   → dashboardLayoutRoute (pathless, id-only)
 *   src/app/…/[id]/page.tsx          → segmento `$id`
 *   src/middleware.ts                → `beforeLoad` em `./guards.ts`
 *
 * As rotas são code-based (sem codegen): a árvore inteira está neste arquivo e
 * cada `component` é carregado sob demanda via `lazyRouteComponent`, o que dá
 * code splitting por página sem arquivos extras.
 */

// ─── helper ───────────────────────────────────────────────────────────────────

/**
 * Açúcar para declarar uma rota filha do layout do dashboard.
 *
 * `const TPath` é essencial: sem ele o parâmetro seria inferido como `string`
 * e o TanStack Router perderia os literais de rota — o que derruba a tipagem
 * de `<Link to>` e `navigate()` na árvore inteira.
 */
function page<const TPath extends string>(
  path: TPath,
  importer: () => Promise<{ default: any }>,
) {
  return createRoute({
    getParentRoute: () => dashboardLayoutRoute,
    path,
    component: lazyRouteComponent(importer),
  })
}

// ─── raiz ─────────────────────────────────────────────────────────────────────

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

// ─── (auth) — rotas públicas ──────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // O middleware da versão Next colocava a URL original em `callbackUrl`;
  // a página de login continua honrando esse contrato.
  validateSearch: z.object({
    callbackUrl: z.string().optional(),
  }),
  beforeLoad: redirectIfAuthed,
  component: lazyRouteComponent(() => import('@/pages/auth/login')),
})

const recuperarSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/recuperar-senha',
  beforeLoad: redirectIfAuthed,
  component: lazyRouteComponent(() => import('@/pages/auth/recuperar-senha')),
})

const redefinirSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir-senha',
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  beforeLoad: redirectIfAuthed,
  component: lazyRouteComponent(() => import('@/pages/auth/redefinir-senha')),
})

/**
 * `/documentacao` fica fora do shell do dashboard — igual à versão Next, onde
 * o arquivo mora em `src/app/documentacao/` e não no grupo `(dashboard)`.
 */
const documentacaoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documentacao',
  component: lazyRouteComponent(() => import('@/pages/documentacao')),
})

// ─── (dashboard) — layout com guard de sessão ─────────────────────────────────

const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Rota "pathless": agrupa e aplica o layout sem aparecer na URL, exatamente
  // como o route group `(dashboard)` do Next.
  //
  // O prefixo `_` segue a convenção do TanStack Router para layouts pathless.
  // Ele NÃO aparece na URL, mas aparece no *id* das rotas filhas — por isso as
  // páginas usam `useParams({ from: '/_dashboard/colaboradores/$id' })`:
  // `to` recebe caminhos, `from` recebe ids.
  id: '_dashboard',
  beforeLoad: requireAuth,
  component: DashboardLayout,
})

const dashboardRoute = page('/dashboard', () => import('@/pages/dashboard'))

// O alias `/dashboard/<rota>` → `/<rota>` vive no `beforeLoad` da rota raiz
// (`./root.tsx`). Como rota splat ele engolia o próprio `/dashboard` — ver o
// comentário lá para o porquê.

// ─── pessoas ──────────────────────────────────────────────────────────────────

const colaboradoresRoute = page('/colaboradores', () => import('@/pages/colaboradores/list'))
const colaboradorNovoRoute = page('/colaboradores/novo', () => import('@/pages/colaboradores/novo'))
const colaboradorRoute = page('/colaboradores/$id', () => import('@/pages/colaboradores/detalhe'))
const colaboradorEditarRoute = page('/colaboradores/$id/editar', () => import('@/pages/colaboradores/editar'))

const estagiariosRoute = page('/estagiarios', () => import('@/pages/estagiarios/list'))
const estagiarioNovoRoute = page('/estagiarios/novo', () => import('@/pages/estagiarios/novo'))
const estagiarioRoute = page('/estagiarios/$id', () => import('@/pages/estagiarios/detalhe'))
const estagiarioEditarRoute = page('/estagiarios/$id/editar', () => import('@/pages/estagiarios/editar'))

const preCadastrosRoute = page('/pre-cadastros', () => import('@/pages/pre-cadastros/list'))
const preCadastroRoute = page('/pre-cadastros/$id', () => import('@/pages/pre-cadastros/detalhe'))

// ─── gestão ───────────────────────────────────────────────────────────────────

const feriasRoute = page('/ferias', () => import('@/pages/ferias'))

const examesRoute = page('/exames', () => import('@/pages/exames/list'))
const exameNovoRoute = page('/exames/novo', () => import('@/pages/exames/novo'))
const exameRoute = page('/exames/$id', () => import('@/pages/exames/detalhe'))
const exameEditarRoute = page('/exames/$id/editar', () => import('@/pages/exames/editar'))

const contratosRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: '/contratos',
  // Mesmo caso de /configuracoes: existiam links para `?tab=templates` que a
  // página ignorava, porque a aba era só `useState`. Agora vem da URL.
  validateSearch: z.object({
    tab: z.enum(['contracts', 'templates']).optional(),
  }),
  component: lazyRouteComponent(() => import('@/pages/contratos/list')),
})
const contratoNovoRoute = page('/contratos/novo', () => import('@/pages/contratos/novo'))
// Segmentos estáticos têm prioridade sobre `$id` no matcher, então
// /contratos/templates nunca é capturado por /contratos/$id.
const templatesRoute = page('/contratos/templates', () => import('@/pages/contratos/templates/list'))
const templateNovoRoute = page('/contratos/templates/novo', () => import('@/pages/contratos/templates/novo'))
const templateRoute = page('/contratos/templates/$id', () => import('@/pages/contratos/templates/detalhe'))
const contratoRoute = page('/contratos/$id', () => import('@/pages/contratos/detalhe'))
const contratoEditarRoute = page('/contratos/$id/editar', () => import('@/pages/contratos/editar'))

const cargosRoute = page('/cargos', () => import('@/pages/cargos'))

const historicoSalarialRoute = page('/historico-salarial', () => import('@/pages/historico-salarial/list'))
const historicoSalarialNovoRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: '/historico-salarial/novo',
  // Pré-seleciona o colaborador quando vindo da ficha dele.
  validateSearch: z.object({
    employeeId: z.string().optional(),
  }),
  component: lazyRouteComponent(() => import('@/pages/historico-salarial/novo')),
})

const beneficiosRoute = page('/beneficios', () => import('@/pages/beneficios'))
const bonusComissoesRoute = page('/bonus-comissoes', () => import('@/pages/bonus-comissoes'))

// ─── organização ──────────────────────────────────────────────────────────────

const calendarioRoute = page('/calendario', () => import('@/pages/calendario'))
const notificacoesRoute = page('/notificacoes', () => import('@/pages/notificacoes'))
const relatoriosRoute = page('/relatorios', () => import('@/pages/relatorios'))

// ─── sistema ──────────────────────────────────────────────────────────────────

const configuracoesRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: '/configuracoes',
  // Na versão Next havia `router.push('/configuracoes?tab=usuarios')`, mas a
  // página ignorava a query e sempre abria em "geral". Aqui a aba passa a ser
  // controlada pela URL de fato.
  validateSearch: z.object({
    tab: z.enum(['geral', 'usuarios', 'api']).optional(),
  }),
  component: lazyRouteComponent(() => import('@/pages/configuracoes/index')),
})

const usuarioNovoRoute = page('/configuracoes/usuarios/novo', () => import('@/pages/configuracoes/usuarios-novo'))
const logsRoute = page('/logs', () => import('@/pages/logs'))
const auditoriaRoute = page('/auditoria', () => import('@/pages/auditoria'))
const manualRoute = page('/manual', () => import('@/pages/manual'))

// ─── árvore ───────────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  recuperarSenhaRoute,
  redefinirSenhaRoute,
  documentacaoRoute,
  dashboardLayoutRoute.addChildren([
    dashboardRoute,

    colaboradoresRoute,
    colaboradorNovoRoute,
    colaboradorRoute,
    colaboradorEditarRoute,

    estagiariosRoute,
    estagiarioNovoRoute,
    estagiarioRoute,
    estagiarioEditarRoute,

    preCadastrosRoute,
    preCadastroRoute,

    feriasRoute,

    examesRoute,
    exameNovoRoute,
    exameRoute,
    exameEditarRoute,

    contratosRoute,
    contratoNovoRoute,
    templatesRoute,
    templateNovoRoute,
    templateRoute,
    contratoRoute,
    contratoEditarRoute,

    cargosRoute,

    historicoSalarialRoute,
    historicoSalarialNovoRoute,

    beneficiosRoute,
    bonusComissoesRoute,

    calendarioRoute,
    notificacoesRoute,
    relatoriosRoute,

    configuracoesRoute,
    usuarioNovoRoute,
    logsRoute,
    auditoriaRoute,
    manualRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: RoutePending,
  defaultErrorComponent: RouteError,
  defaultNotFoundComponent: NotFound,
  scrollRestoration: true,
})

// Habilita a tipagem de `<Link to>`, `useParams`, `useSearch` e `navigate`
// para toda a árvore acima.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
